import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useCriancas } from '../hooks/useCriancas';
import { useTurmas } from '../hooks/useTurmas';
import { useCreches } from '../hooks/useCreches';
import IAConfiguracaoPanel from './IAConfiguracaoPanel';
import LizMultimodalLive from './LizMultimodalLive';

interface Insight {
  id: number;
  id_creche?: number;
  id_turma?: number;
  id_aluno?: number;
  tipo: string;
  descricao: string;
  dados_brutos?: Record<string, unknown>;
  criado_em?: string;
}

interface Alerta {
  id: number;
  id_creche?: number;
  id_turma?: number;
  id_aluno?: number;
  tipo: string;
  status: string;
  mensagem: string;
  dados_brutos?: Record<string, unknown>;
  id_responsavel?: number;
  criado_em?: string;
}

interface MaterialIA {
  id: number;
  titulo: string;
  descricao?: string;
  tipo_arquivo?: string;
  tipo_material?: string;
  audiencia?: string;
  url_link?: string;
  url_video?: string;
  status_validacao: 'pendente' | 'aprovado' | 'rejeitado';
  nome_arquivo_original?: string;
  resumo_validado?: string;
  justificativa_validacao?: string;
}

interface HistoricoValidacao {
  data: string;
  status_anterior?: string;
  status_novo?: string;
  audiencia?: string;
  motivo?: string;
  id_usuario?: number;
  usuario?: string;
}

interface HistoricoTimelineItem {
  id_material: number;
  titulo_material: string;
  status_atual_material?: string;
  audiencia_material?: string;
  data?: string;
  status_anterior?: string;
  status_novo?: string;
  audiencia?: string;
  motivo?: string;
  id_usuario?: number;
  usuario?: string;
}

interface ChatMensagem {
  id: number;
  papel: 'usuario' | 'assistente';
  texto: string;
  horario: string;
}

declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}

const TIPOS_INSIGHT = [
  { value: 'rotina', label: 'Rotina e Brincadeiras' },
  { value: 'frequencia', label: 'Frequência do Bebê' },
  { value: 'humor', label: 'Como se sentiu (Humor)' },
  { value: 'alimentacao', label: 'Alimentação / Lanchinho' },
  { value: 'sono', label: 'Soninho' },
  { value: 'atendimento_especial', label: 'Muito Cuidado / Atenção Especial' },
  { value: 'financeiro', label: 'Burocrático/Financeiro' },
  { value: 'outro', label: 'Outra observação carinhosa' },
];

const TIPOS_ALERTA = [
  { value: 'atencao', label: 'Atenção e Cuidado' },
  { value: 'importante', label: 'Assunto Importante/Saúde' },
  { value: 'critico', label: 'Urgente / Mamar / Febre' },
];

const STATUS_ALERTA = [
  { value: 'aberto', label: 'Aguardando Cuidado' },
  { value: 'em_andamento', label: 'Sendo Cuidado Agora' },
  { value: 'resolvido', label: 'Tudo Resolvido e Bem' },
];

export default function AssistenteIAPage() {
  const { creches } = useCreches();
  const { turmas } = useTurmas();
  const { criancas } = useCriancas();
  const recognitionRef = useRef<any>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvandoInsight, setSalvandoInsight] = useState(false);
  const [salvandoAlerta, setSalvandoAlerta] = useState(false);
  const [enviandoPergunta, setEnviandoPergunta] = useState(false);
  const [ouvindoPergunta, setOuvindoPergunta] = useState(false);
  const [subindoMaterial, setSubindoMaterial] = useState(false);
  const [mensagensChat, setMensagensChat] = useState<ChatMensagem[]>([]);
  const [pergunta, setPergunta] = useState('');
  const [materiais, setMateriais] = useState<MaterialIA[]>([]);
  const [filtroStatusMaterial, setFiltroStatusMaterial] = useState<'todos' | 'pendente' | 'aprovado' | 'rejeitado'>('todos');
  const [filtros, setFiltros] = useState({ id_turma: 0, id_aluno: 0, data_inicio: '', data_fim: '' });
  const [uploadForm, setUploadForm] = useState({ titulo: '', descricao: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
  const [arquivoUpload, setArquivoUpload] = useState<File | null>(null);
  const [linkForm, setLinkForm] = useState({ titulo: '', descricao: '', url_link: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
  const [videoForm, setVideoForm] = useState({ titulo: '', descricao: '', url_video: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
  const [adicionandoLink, setAdicionandoLink] = useState(false);
  const [adicionandoVideo, setAdicionandoVideo] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<'arquivo' | 'link' | 'video'>('arquivo');
  const [historicosMateriais, setHistoricosMateriais] = useState<Record<number, HistoricoValidacao[]>>({});
  const [materialHistoricoAberto, setMaterialHistoricoAberto] = useState<number | null>(null);
  const [timelineHistorico, setTimelineHistorico] = useState<HistoricoTimelineItem[]>([]);
  const [timelineCarregando, setTimelineCarregando] = useState(false);
  const [filtrosTimeline, setFiltrosTimeline] = useState({ id_usuario: '', data_inicio: '', data_fim: '' });
  const [insightForm, setInsightForm] = useState<Partial<Insight>>({ tipo: 'rotina', id_turma: 0, id_aluno: 0, descricao: '' });
  const [alertaForm, setAlertaForm] = useState<Partial<Alerta>>({ tipo: 'atencao', status: 'aberto', id_turma: 0, id_aluno: 0, id_responsavel: 0, mensagem: '' });
  const [config, setConfig] = useState<any>({ nome_assistente: 'Assistente da Creche', perfil_conversa_interna: 'parceria_pedagogica' });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = {
        id_turma: Number(filtros.id_turma || 0) || undefined,
        id_aluno: Number(filtros.id_aluno || 0) || undefined,
        data_inicio: filtros.data_inicio || undefined,
        data_fim: filtros.data_fim || undefined,
      };
      const [resInsights, resAlertas, resConfig] = await Promise.all([
        api.get<Insight[]>('/ia/insights', { params }),
        api.get<Alerta[]>('/ia/alertas', { params }),
        api.get<any>('/ia/configuracao').catch(() => ({ data: null })),
      ]);
      setInsights(resInsights.data || []);
      setAlertas(resAlertas.data || []);
      if (resConfig?.data) setConfig(resConfig.data);
      setErro('');
    } catch {
      setErro('Não foi possível carregar os dados do Assistente de IA.');
      setInsights([]);
      setAlertas([]);
    } finally {
      setCarregando(false);
    }
  }, [filtros.data_fim, filtros.data_inicio, filtros.id_aluno, filtros.id_turma]);

  const carregarMateriais = useCallback(async () => {
    try {
      const params = filtroStatusMaterial === 'todos' ? undefined : { status_validacao: filtroStatusMaterial };
      const res = await api.get<MaterialIA[]>('/ia/materiais', { params });
      setMateriais(res.data || []);
    } catch {
      setMateriais([]);
    }
  }, [filtroStatusMaterial]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    carregarMateriais();
  }, [carregarMateriais]);

  const carregarTimelineHistorico = useCallback(async () => {
    setTimelineCarregando(true);
    try {
      const params = {
        id_usuario: filtrosTimeline.id_usuario ? Number(filtrosTimeline.id_usuario) : undefined,
        data_inicio: filtrosTimeline.data_inicio || undefined,
        data_fim: filtrosTimeline.data_fim || undefined,
      };
      const res = await api.get('/ia/materiais/historicos', { params });
      setTimelineHistorico(Array.isArray(res?.data?.itens) ? res.data.itens : []);
      setErro('');
    } catch {
      setErro('Não foi possível carregar a timeline de validações.');
      setTimelineHistorico([]);
    } finally {
      setTimelineCarregando(false);
    }
  }, [filtrosTimeline.data_fim, filtrosTimeline.data_inicio, filtrosTimeline.id_usuario]);

  useEffect(() => {
    void carregarTimelineHistorico();
  }, [carregarTimelineHistorico]);

  function nomeTurma(id?: number) {
    return turmas.find((item) => item.id === id)?.nome || 'Não vinculada';
  }

  function nomeAluno(id?: number) {
    return criancas.find((item) => item.id === id)?.nome || 'Não vinculado';
  }

  function nomeCreche(id?: number) {
    return creches.find((item) => item.id === id)?.nome || 'Creche atual';
  }

  const aprovadoresDisponiveis = Array.from(
    new Map(
      timelineHistorico
        .filter((item) => item.id_usuario || item.usuario)
        .map((item) => [
          String(item.id_usuario || item.usuario || ''),
          {
            id_usuario: item.id_usuario,
            usuario: item.usuario || `Usuário ${item.id_usuario}`,
          },
        ]),
    ).values(),
  );

  const escolherVozFeminina = useCallback((vozPreferida?: string): SpeechSynthesisVoice | null => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const vozes = window.speechSynthesis.getVoices();

    if (vozPreferida) {
      const configurada = vozes.find((v) => v.name === vozPreferida);
      if (configurada) return configurada;
    }

    const femininas = vozes.filter((v) =>
      v.lang.startsWith('pt')
      && /female|feminino|\bf\b|francisca|vitoria|vitória|luciana|isabel|helena|leila|ana\b|maria\b|google.*pt.*female|edgetts.*pt.*female/i.test(v.name),
    );
    if (femininas.length) return femininas[0];

    const ptbr = vozes.filter((v) => v.lang === 'pt-BR' || v.lang === 'pt_BR');
    if (ptbr.length) return ptbr[0];

    const pt = vozes.filter((v) => v.lang.startsWith('pt'));
    if (pt.length) return pt[0];

    return null;
  }, []);

  function ehComandoPararFala(texto: string): boolean {
    return /(pare de falar|pare|sil[eê]ncio|silencio|fique quiet[ao]|pode parar|interrompa|interromper)/i.test(String(texto || '').trim());
  }

  function falarResposta(texto: string, voz?: string) {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const executar = () => {
      const utterance = new SpeechSynthesisUtterance(texto);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.92;
      utterance.pitch = 1.2;
      utterance.volume = 1;
      const voice = escolherVozFeminina(voz);
      if (voice) utterance.voice = voice;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.addEventListener('voiceschanged', executar, { once: true });
    } else {
      executar();
    }
  }

  function iniciarPerguntaPorAudio() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setErro('Reconhecimento de voz não disponível neste navegador.');
      return;
    }

    const recognition = new Recognition();
    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setOuvindoPergunta(true);
    recognition.onend = () => setOuvindoPergunta(false);
    recognition.onerror = () => {
      setOuvindoPergunta(false);
      setErro('Não foi possível capturar a pergunta por áudio.');
    };
    recognition.onresult = (event: any) => {
      const texto = event?.results?.[0]?.[0]?.transcript || '';
      setPergunta(texto);
    };
    recognitionRef.current = recognition;
    recognition.start();
  }

  async function salvarInsight() {
    if (!insightForm.tipo || !insightForm.descricao?.trim()) {
      setErro('Preencha tipo e descrição do insight.');
      return;
    }

    setSalvandoInsight(true);
    try {
      await api.post('/ia/insights', {
        tipo: insightForm.tipo,
        descricao: insightForm.descricao,
        id_turma: Number(insightForm.id_turma || 0) || null,
        id_aluno: Number(insightForm.id_aluno || 0) || null,
        dados_brutos: {},
      });
      setInsightForm({ tipo: 'rotina', id_turma: 0, id_aluno: 0, descricao: '' });
      await carregar();
    } catch {
      setErro('Erro ao salvar insight de IA.');
    } finally {
      setSalvandoInsight(false);
    }
  }

  async function salvarAlerta() {
    if (!alertaForm.tipo || !alertaForm.status || !alertaForm.mensagem?.trim()) {
      setErro('Preencha tipo, status e mensagem do alerta.');
      return;
    }

    setSalvandoAlerta(true);
    try {
      await api.post('/ia/alertas', {
        tipo: alertaForm.tipo,
        status: alertaForm.status,
        mensagem: alertaForm.mensagem,
        id_turma: Number(alertaForm.id_turma || 0) || null,
        id_aluno: Number(alertaForm.id_aluno || 0) || null,
        id_responsavel: Number(alertaForm.id_responsavel || 0) || null,
        dados_brutos: {},
      });
      setAlertaForm({ tipo: 'atencao', status: 'aberto', id_turma: 0, id_aluno: 0, id_responsavel: 0, mensagem: '' });
      await carregar();
    } catch {
      setErro('Erro ao salvar alerta de IA.');
    } finally {
      setSalvandoAlerta(false);
    }
  }

  async function atualizarStatusAlerta(id: number, status: string) {
    try {
      await api.put(`/ia/alertas/${id}`, { status });
      await carregar();
    } catch {
      setErro('Erro ao atualizar status do alerta.');
    }
  }

  async function enviarPergunta() {
    const texto = pergunta.trim();
    if (!texto) {
      setErro('Digite uma pergunta para o chat da IA.');
      return;
    }

    const mensagemUsuario: ChatMensagem = {
      id: Date.now(),
      papel: 'usuario',
      texto,
      horario: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
    setMensagensChat((prev) => [...prev, mensagemUsuario]);
    setPergunta('');

    if (ehComandoPararFala(texto)) {
      try {
        window.speechSynthesis?.cancel();
      } catch {}
      setErro('');
      return;
    }

    setEnviandoPergunta(true);

    try {
      const res = await api.post('/ia/chat', {
        pergunta: texto,
        id_turma: Number(filtros.id_turma || 0) || undefined,
        id_aluno: Number(filtros.id_aluno || 0) || undefined,
        data_inicio: filtros.data_inicio || undefined,
        data_fim: filtros.data_fim || undefined,
      });

      const resposta = String(res?.data?.resposta || 'Não consegui gerar resposta agora.');
      const audio = res?.data?.audio;
      if (resposta.trim()) {
        setMensagensChat((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            papel: 'assistente',
            texto: resposta,
            horario: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      }
      if (audio?.saida_habilitada && resposta.trim()) {
        falarResposta(resposta, audio?.voz);
      }
      setErro('');
    } catch {
      setErro('Não foi possível consultar o chat da IA.');
    } finally {
      setEnviandoPergunta(false);
    }
  }

  async function subirMaterial() {
    if (!arquivoUpload) {
      setErro('Selecione um arquivo PDF, Word, planilha ou vídeo para enviar.');
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', arquivoUpload);
    formData.append('titulo', uploadForm.titulo || arquivoUpload.name);
    formData.append('descricao', uploadForm.descricao);
    formData.append('palavras_chave', uploadForm.palavras_chave);
    formData.append('resumo_validado', uploadForm.resumo_validado);
    formData.append('audiencia', uploadForm.audiencia);

    setSubindoMaterial(true);
    try {
      await api.post('/ia/materiais/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadForm({ titulo: '', descricao: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
      setArquivoUpload(null);
      await carregarMateriais();
      setErro('');
    } catch {
      setErro('Não foi possível enviar o material para treinamento.');
    } finally {
      setSubindoMaterial(false);
    }
  }

  async function validarMaterial(material: MaterialIA, status: 'aprovado' | 'rejeitado') {
    try {
      const motivoPadrao = status === 'rejeitado'
        ? 'Material rejeitado na validação de gestores.'
        : 'Material aprovado para base de conhecimento do assistente.';
      const motivo = window.prompt('Informe o motivo desta validação:', material.justificativa_validacao || motivoPadrao) || motivoPadrao;

      await api.put(`/ia/materiais/${material.id}/validacao`, {
        status_validacao: status,
        justificativa_validacao: motivo,
        resumo_validado: material.resumo_validado || uploadForm.resumo_validado || '',
        audiencia: material.audiencia || 'interno',
      });
      await carregarMateriais();
      if (materialHistoricoAberto === material.id) {
        await carregarHistoricoMaterial(material.id);
      }
      setErro('');
    } catch {
      setErro('Falha ao validar material. Apenas gestores/admin podem validar.');
    }
  }

  async function carregarHistoricoMaterial(idMaterial: number) {
    try {
      const res = await api.get(`/ia/materiais/${idMaterial}/historico`);
      const historico = Array.isArray(res?.data?.historico_validacoes) ? res.data.historico_validacoes : [];
      setHistoricosMateriais((prev) => ({ ...prev, [idMaterial]: historico }));
      setMaterialHistoricoAberto(idMaterial);
    } catch {
      setErro('Não foi possível carregar o histórico de validações deste material.');
    }
  }

  function alternarHistoricoMaterial(idMaterial: number) {
    if (materialHistoricoAberto === idMaterial) {
      setMaterialHistoricoAberto(null);
      return;
    }
    void carregarHistoricoMaterial(idMaterial);
  }

  async function adicionarLink() {
    const url = linkForm.url_link.trim();
    if (!url) {
      setErro('Informe a URL do link (ex: https://exemplo.com).');
      return;
    }

    setAdicionandoLink(true);
    try {
      await api.post('/ia/materiais/link', {
        titulo: linkForm.titulo || url.split('/')[2],
        descricao: linkForm.descricao,
        url_link: url,
        palavras_chave: linkForm.palavras_chave,
        resumo_validado: linkForm.resumo_validado,
        audiencia: linkForm.audiencia,
      });
      setLinkForm({ titulo: '', descricao: '', url_link: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
      await carregarMateriais();
      setErro('');
    } catch {
      setErro('Não foi possível adicionar o link. Verifique a URL.');
    } finally {
      setAdicionandoLink(false);
    }
  }

  async function adicionarVideo() {
    const url = videoForm.url_video.trim();
    if (!url) {
      setErro('Informe a URL do vídeo (ex: https://youtube.com/watch?v=...).');
      return;
    }

    setAdicionandoVideo(true);
    try {
      await api.post('/ia/materiais/video', {
        titulo: videoForm.titulo || 'Vídeo de Treinamento',
        descricao: videoForm.descricao,
        url_video: url,
        palavras_chave: videoForm.palavras_chave,
        resumo_validado: videoForm.resumo_validado,
        audiencia: videoForm.audiencia,
      });
      setVideoForm({ titulo: '', descricao: '', url_video: '', palavras_chave: '', resumo_validado: '', audiencia: 'interno' });
      await carregarMateriais();
      setErro('');
    } catch {
      setErro('Não foi possível adicionar o vídeo. Verifique a URL.');
    } finally {
      setAdicionandoVideo(false);
    }
  }

  const alertasCriticos = alertas.filter((a) => a.tipo === 'critico' && a.status !== 'resolvido').length;

  // Nome da assistente DESTA creche (batizável pelo gestor). "Liz" é a assistente
  // comercial do SaaS — nunca a da creche; por isso o default aqui é genérico.
  const nomeAssistente = (config?.nome_assistente || 'Assistente da Creche').split(' - ')[0].trim() || 'Assistente da Creche';
  const jaBatizada = !/^(liz|assistente da creche)$/i.test(nomeAssistente);
  const nomeAcolhimento = jaBatizada ? nomeAssistente : 'a sua Assistente';

  return (
    <div className="ia-page">
      <div className="pagina-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <img src={config?.avatar_url || '/assistente_avatar.svg'} alt={`Assistente da creche${jaBatizada ? ` — ${nomeAssistente}` : ''}`} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #ff7fa4' }} />
        <div>
          <h2 className="pagina-titulo">Acolhimento com {nomeAcolhimento}</h2>
          <p className="pagina-subtitulo">
            {!jaBatizada && 'Cada creche batiza a sua própria assistente com o nome que quiser. '}
            Um espaço seguro e acolhedor para tirar dúvidas, registrar emoções e conversar com a assistente da sua creche.
          </p>
        </div>
      </div>

      {erro && <div className="alerta alerta-erro">{erro} <button onClick={() => setErro('')}>✕</button></div>}

      <IAConfiguracaoPanel />

      <section className="ia-box">
        <h3>Do que estamos cuidando agora? (Filtros de Contexto)</h3>
        <div className="form-grid ia-filtros-grid">
          <label>Qual Turminha?
            <select value={filtros.id_turma} onChange={(e) => setFiltros((prev) => ({ ...prev, id_turma: Number(e.target.value) }))}>
              <option value={0}>Todas as turminhas</option>
              {turmas.map((turma) => <option key={turma.id} value={turma.id}>{turma.nome}</option>)}
            </select>
          </label>
          <label>Qual Criança?
            <select value={filtros.id_aluno} onChange={(e) => setFiltros((prev) => ({ ...prev, id_aluno: Number(e.target.value) }))}>
              <option value={0}>Todas as crianças</option>
              {criancas.map((crianca) => <option key={crianca.id} value={crianca.id}>{crianca.nome}</option>)}
            </select>
          </label>
          <label>Início deste cuidado
            <input type="date" value={filtros.data_inicio} onChange={(e) => setFiltros((prev) => ({ ...prev, data_inicio: e.target.value }))} />
          </label>
          <label>Fim até
            <input type="date" value={filtros.data_fim} onChange={(e) => setFiltros((prev) => ({ ...prev, data_fim: e.target.value }))} />
          </label>
        </div>
      </section>

      <section className="ia-box ia-chat-box">
        <div className="ia-section-header">
          <div>
            <h3>Conversar com {config.nome_assistente}</h3>
            <p className="texto-ajuda">Aqui você pode pedir ajuda sobre a rotina, conselhos sobre o humor de uma criança ou tirar dúvidas da creche de forma natural e gentil.</p>
          </div>
        </div>

        {/* Interface Live Voice (Gemini 2.0 Flash) */}
        <LizMultimodalLive
          assistantName={config.nome_assistente.split(' - ')[0]}
          personality={config.perfil_conversa_interna}
          avatarUrl={config?.avatar_url || '/assistente_avatar.svg'}
        />

        <div className="ia-chat-resumo">
          <span className="badge badge-vencido">Cuidados Especiais: {alertasCriticos}</span>
          <span className="badge badge-pendente">Ocorrências Diárias: {insights.length}</span>
          <span className="badge badge-pago">Sabedoria da {nomeAssistente}: {materiais.filter((m) => m.status_validacao === 'aprovado').length} ref.</span>
        </div>
        <div className="ia-chat-historico">
          {mensagensChat.length === 0 ? (
            <p className="texto-ajuda">Diga "Oi!" ou pergunte como podemos melhorar o dia da turma.</p>
          ) : (
            mensagensChat.map((msg) => (
              <article key={msg.id} className={`ia-chat-msg ia-chat-msg-${msg.papel}`}>
                <strong>{msg.papel === 'usuario' ? 'Você' : `${nomeAssistente} 💝`}</strong>
                <p>{msg.texto}</p>
                <small>{msg.horario}</small>
              </article>
            ))
          )}
        </div>
        <div className="ia-chat-inputs">
          <textarea
            rows={3}
            placeholder="Ex.: Quais ações devo priorizar hoje para a turma Jardim I?"
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
          />
          <button className="btn-primario" onClick={enviarPergunta} disabled={enviandoPergunta}>
            {enviandoPergunta ? 'Consultando...' : 'Enviar Pergunta'}
          </button>
        </div>
      </section>

      <div className="ia-grid-topo">
        <section className="ia-box ia-box-insight">
          <h3>Como e o que a Criança viveu hoje? (Observação Lúdica)</h3>
          <div className="form-grid">
            <label>Tipo
              <select value={insightForm.tipo ?? 'rotina'} onChange={(e) => setInsightForm((prev) => ({ ...prev, tipo: e.target.value }))}>
                {TIPOS_INSIGHT.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
              </select>
            </label>
            <label>Turma
              <select value={Number(insightForm.id_turma || 0)} onChange={(e) => setInsightForm((prev) => ({ ...prev, id_turma: Number(e.target.value) }))}>
                <option value={0}>Não vincular</option>
                {turmas.map((turma) => <option key={turma.id} value={turma.id}>{turma.nome}</option>)}
              </select>
            </label>
            <label>Aluno
              <select value={Number(insightForm.id_aluno || 0)} onChange={(e) => setInsightForm((prev) => ({ ...prev, id_aluno: Number(e.target.value) }))}>
                <option value={0}>Não vincular</option>
                {criancas.map((crianca) => <option key={crianca.id} value={crianca.id}>{crianca.nome}</option>)}
              </select>
            </label>
            <label>Descrição
              <textarea rows={4} value={insightForm.descricao ?? ''} onChange={(e) => setInsightForm((prev) => ({ ...prev, descricao: e.target.value }))} />
            </label>
            <button className="btn-primario" onClick={salvarInsight} disabled={salvandoInsight}>{salvandoInsight ? 'Salvando...' : 'Salvar Insight'}</button>
          </div>
        </section>

        <section className="ia-box ia-box-alerta">
          <h3>Pedir Atenção ou Cuidado Especial</h3>
          <div className="form-grid">
            <label>Tipo
              <select value={alertaForm.tipo ?? 'atencao'} onChange={(e) => setAlertaForm((prev) => ({ ...prev, tipo: e.target.value }))}>
                {TIPOS_ALERTA.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
              </select>
            </label>
            <label>Status
              <select value={alertaForm.status ?? 'aberto'} onChange={(e) => setAlertaForm((prev) => ({ ...prev, status: e.target.value }))}>
                {STATUS_ALERTA.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
              </select>
            </label>
            <label>Turma
              <select value={Number(alertaForm.id_turma || 0)} onChange={(e) => setAlertaForm((prev) => ({ ...prev, id_turma: Number(e.target.value) }))}>
                <option value={0}>Não vincular</option>
                {turmas.map((turma) => <option key={turma.id} value={turma.id}>{turma.nome}</option>)}
              </select>
            </label>
            <label>Aluno
              <select value={Number(alertaForm.id_aluno || 0)} onChange={(e) => setAlertaForm((prev) => ({ ...prev, id_aluno: Number(e.target.value) }))}>
                <option value={0}>Não vincular</option>
                {criancas.map((crianca) => <option key={crianca.id} value={crianca.id}>{crianca.nome}</option>)}
              </select>
            </label>
            <label>Mensagem
              <textarea rows={4} value={alertaForm.mensagem ?? ''} onChange={(e) => setAlertaForm((prev) => ({ ...prev, mensagem: e.target.value }))} />
            </label>
            <button className="btn-primario" onClick={salvarAlerta} disabled={salvandoAlerta}>{salvandoAlerta ? 'Salvando...' : 'Salvar Alerta'}</button>
          </div>
        </section>
      </div>

      <section className="ia-box">
        <h3>Sabedoria da {nomeAssistente} (O que ela aprendeu conosco)</h3>
        <p className="texto-ajuda">Ensine a {nomeAssistente} colocando materiais maravilhosos (PDFs, vídeos e ideias) para ela ler e usar na hora de ajudar nossa equipe.</p>
        
        <div className="ia-training-tabs">
          <button 
            className={`ia-tab ${abaAtiva === 'arquivo' ? 'ia-tab-ativo' : ''}`}
            onClick={() => setAbaAtiva('arquivo')}
          >
            📎 Arquivo
          </button>
          <button 
            className={`ia-tab ${abaAtiva === 'link' ? 'ia-tab-ativo' : ''}`}
            onClick={() => setAbaAtiva('link')}
          >
            🔗 Link
          </button>
          <button 
            className={`ia-tab ${abaAtiva === 'video' ? 'ia-tab-ativo' : ''}`}
            onClick={() => setAbaAtiva('video')}
          >
            📹 Vídeo
          </button>
        </div>

        {abaAtiva === 'arquivo' && (
          <div className="form-grid ia-upload-grid">
            <label>Arquivo (PDF, Word, Planilha ou Vídeo)
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.mp4,.avi,.mov,.mkv"
                onChange={(e) => setArquivoUpload(e.target.files?.[0] || null)}
              />
            </label>
            <label>Título
              <input value={uploadForm.titulo} onChange={(e) => setUploadForm((prev) => ({ ...prev, titulo: e.target.value }))} />
            </label>
            <label>Descrição
              <textarea rows={3} value={uploadForm.descricao} onChange={(e) => setUploadForm((prev) => ({ ...prev, descricao: e.target.value }))} />
            </label>
            <label>Palavras-chave (separadas por vírgula)
              <input value={uploadForm.palavras_chave} onChange={(e) => setUploadForm((prev) => ({ ...prev, palavras_chave: e.target.value }))} />
            </label>
            <label>Resumo técnico para validação
              <textarea rows={3} value={uploadForm.resumo_validado} onChange={(e) => setUploadForm((prev) => ({ ...prev, resumo_validado: e.target.value }))} />
            </label>
            <label>Audiência do material
              <select value={uploadForm.audiencia} onChange={(e) => setUploadForm((prev) => ({ ...prev, audiencia: e.target.value }))}>
                <option value="interno">Somente interno</option>
                <option value="publico">Somente público</option>
                <option value="ambos">Público e interno</option>
              </select>
            </label>
            <button className="btn-primario" onClick={subirMaterial} disabled={subindoMaterial}>
              {subindoMaterial ? 'Enviando...' : 'Enviar Arquivo para Validação'}
            </button>
          </div>
        )}

        {abaAtiva === 'link' && (
          <div className="form-grid ia-upload-grid">
            <label>URL do Link
              <input 
                type="url"
                placeholder="https://exemplo.com"
                value={linkForm.url_link} 
                onChange={(e) => setLinkForm((prev) => ({ ...prev, url_link: e.target.value }))} 
              />
            </label>
            <label>Título (opcional)
              <input value={linkForm.titulo} onChange={(e) => setLinkForm((prev) => ({ ...prev, titulo: e.target.value }))} />
            </label>
            <label>Descrição
              <textarea rows={3} value={linkForm.descricao} onChange={(e) => setLinkForm((prev) => ({ ...prev, descricao: e.target.value }))} />
            </label>
            <label>Palavras-chave (separadas por vírgula)
              <input value={linkForm.palavras_chave} onChange={(e) => setLinkForm((prev) => ({ ...prev, palavras_chave: e.target.value }))} />
            </label>
            <label>Resumo técnico para validação
              <textarea rows={3} value={linkForm.resumo_validado} onChange={(e) => setLinkForm((prev) => ({ ...prev, resumo_validado: e.target.value }))} />
            </label>
            <label>Audiência do material
              <select value={linkForm.audiencia} onChange={(e) => setLinkForm((prev) => ({ ...prev, audiencia: e.target.value }))}>
                <option value="interno">Somente interno</option>
                <option value="publico">Somente público</option>
                <option value="ambos">Público e interno</option>
              </select>
            </label>
            <button className="btn-primario" onClick={adicionarLink} disabled={adicionandoLink}>
              {adicionandoLink ? 'Adicionando...' : 'Adicionar Link para Validação'}
            </button>
          </div>
        )}

        {abaAtiva === 'video' && (
          <div className="form-grid ia-upload-grid">
            <label>URL do Vídeo (YouTube, Vimeo, etc)
              <input 
                type="url"
                placeholder="https://youtube.com/watch?v=..."
                value={videoForm.url_video} 
                onChange={(e) => setVideoForm((prev) => ({ ...prev, url_video: e.target.value }))} 
              />
            </label>
            <label>Título (opcional)
              <input value={videoForm.titulo} onChange={(e) => setVideoForm((prev) => ({ ...prev, titulo: e.target.value }))} />
            </label>
            <label>Descrição
              <textarea rows={3} value={videoForm.descricao} onChange={(e) => setVideoForm((prev) => ({ ...prev, descricao: e.target.value }))} />
            </label>
            <label>Palavras-chave (separadas por vírgula)
              <input value={videoForm.palavras_chave} onChange={(e) => setVideoForm((prev) => ({ ...prev, palavras_chave: e.target.value }))} />
            </label>
            <label>Resumo técnico para validação
              <textarea rows={3} value={videoForm.resumo_validado} onChange={(e) => setVideoForm((prev) => ({ ...prev, resumo_validado: e.target.value }))} />
            </label>
            <label>Audiência do material
              <select value={videoForm.audiencia} onChange={(e) => setVideoForm((prev) => ({ ...prev, audiencia: e.target.value }))}>
                <option value="interno">Somente interno</option>
                <option value="publico">Somente público</option>
                <option value="ambos">Público e interno</option>
              </select>
            </label>
            <button className="btn-primario" onClick={adicionarVideo} disabled={adicionandoVideo}>
              {adicionandoVideo ? 'Adicionando...' : 'Adicionar Vídeo para Validação'}
            </button>
          </div>
        )}

        <div className="ia-material-header">
          <h4>Materiais na Base de Conhecimento</h4>
          <select
            value={filtroStatusMaterial}
            onChange={(e) => setFiltroStatusMaterial(e.target.value as 'todos' | 'pendente' | 'aprovado' | 'rejeitado')}
          >
            <option value="todos">Todos</option>
            <option value="pendente">Pendentes</option>
            <option value="aprovado">Aprovados</option>
            <option value="rejeitado">Rejeitados</option>
          </select>
        </div>

        <div className="ia-lista">
          {materiais.length === 0 ? (
            <p className="texto-ajuda">Nenhum material encontrado para o filtro selecionado.</p>
          ) : (
            materiais.map((material) => (
              <article key={material.id} className="ia-card ia-card-material">
                <div className="ia-card-topo">
                  <strong>{material.titulo}</strong>
                  <span className={`badge badge-${material.status_validacao === 'aprovado' ? 'pago' : material.status_validacao === 'rejeitado' ? 'vencido' : 'pendente'}`}>
                    {material.status_validacao}
                  </span>
                </div>
                <p>{material.descricao || 'Sem descrição.'}</p>
                <div className="ia-meta">
                  Origem: {material.nome_arquivo_original || material.url_link || material.url_video || '-'} | Tipo: {material.tipo_material || material.tipo_arquivo || '-'} | Audiência: {material.audiencia || 'interno'}
                </div>
                <div className="ia-material-actions">
                  <button className="btn-primario" onClick={() => validarMaterial(material, 'aprovado')}>Aprovar</button>
                  <button className="btn-secundario" onClick={() => validarMaterial(material, 'rejeitado')}>Rejeitar</button>
                  <button className="btn-secundario" onClick={() => alternarHistoricoMaterial(material.id)}>
                    {materialHistoricoAberto === material.id ? 'Ocultar histórico' : 'Ver histórico'}
                  </button>
                </div>
                {materialHistoricoAberto === material.id && (
                  <div className="ia-meta" style={{ marginTop: '.75rem' }}>
                    {(historicosMateriais[material.id] || []).length === 0 ? (
                      <p className="texto-ajuda">Sem histórico registrado.</p>
                    ) : (
                      (historicosMateriais[material.id] || []).map((item, index) => (
                        <p key={`${material.id}-${index}`}>
                          {new Date(item.data).toLocaleString('pt-BR')} | {item.status_anterior || '-'} {'->'} {item.status_novo || '-'} | audiência: {item.audiencia || '-'} | por: {item.usuario || item.id_usuario || 'usuário'} | motivo: {item.motivo || '-'}
                        </p>
                      ))
                    )}
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </section>

      <section className="ia-box">
        <div className="ia-section-header">
          <div>
            <h3>Timeline de Validações</h3>
            <p className="texto-ajuda">Histórico completo de aprovações/rejeições com filtros por aprovador e período.</p>
          </div>
          <button className="btn-secundario" onClick={() => carregarTimelineHistorico()} disabled={timelineCarregando}>
            {timelineCarregando ? 'Atualizando...' : 'Atualizar timeline'}
          </button>
        </div>

        <div className="form-grid ia-filtros-grid">
          <label>Aprovador
            <select
              value={filtrosTimeline.id_usuario}
              onChange={(e) => setFiltrosTimeline((prev) => ({ ...prev, id_usuario: e.target.value }))}
            >
              <option value="">Todos</option>
              {aprovadoresDisponiveis.map((aprovador) => (
                <option key={String(aprovador.id_usuario || aprovador.usuario)} value={String(aprovador.id_usuario || '')}>
                  {aprovador.usuario}
                </option>
              ))}
            </select>
          </label>
          <label>Data inicial
            <input
              type="date"
              value={filtrosTimeline.data_inicio}
              onChange={(e) => setFiltrosTimeline((prev) => ({ ...prev, data_inicio: e.target.value }))}
            />
          </label>
          <label>Data final
            <input
              type="date"
              value={filtrosTimeline.data_fim}
              onChange={(e) => setFiltrosTimeline((prev) => ({ ...prev, data_fim: e.target.value }))}
            />
          </label>
        </div>

        <div className="ia-timeline">
          {timelineCarregando ? (
            <p className="texto-ajuda">Carregando histórico...</p>
          ) : timelineHistorico.length === 0 ? (
            <p className="texto-ajuda">Nenhum evento encontrado para os filtros selecionados.</p>
          ) : (
            timelineHistorico.map((evento, index) => (
              <article key={`${evento.id_material}-${evento.data || index}-${index}`} className="ia-timeline-item">
                <div className="ia-timeline-dot" />
                <div className="ia-timeline-content">
                  <div className="ia-card-topo">
                    <strong>{evento.titulo_material}</strong>
                    <span className={`badge badge-${evento.status_novo === 'aprovado' ? 'pago' : evento.status_novo === 'rejeitado' ? 'vencido' : 'pendente'}`}>
                      {evento.status_novo || 'sem status'}
                    </span>
                  </div>
                  <p>
                    {evento.status_anterior || '-'} {'->'} {evento.status_novo || '-'} | audiência: {evento.audiencia || evento.audiencia_material || '-'}
                  </p>
                  <div className="ia-meta">
                    {evento.data ? new Date(evento.data).toLocaleString('pt-BR') : '-'} | aprovador: {evento.usuario || evento.id_usuario || 'não identificado'}
                  </div>
                  <div className="ia-meta">
                    Motivo: {evento.motivo || '-'}
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <div className="ia-grid-listas">
        <section className="ia-box">
          <h3>Insights Gerados</h3>
          {carregando ? <p className="texto-ajuda">Carregando insights...</p> : insights.length === 0 ? <p className="texto-ajuda">Nenhum insight registrado.</p> : (
            <div className="ia-lista">
              {insights.map((insight) => (
                <article key={insight.id} className="ia-card">
                  <div className="ia-card-topo">
                    <span className="badge badge-pago">{insight.tipo}</span>
                    <span className="texto-ajuda">{nomeCreche(insight.id_creche)}</span>
                  </div>
                  <p>{insight.descricao}</p>
                  <div className="ia-meta">Turma: {nomeTurma(insight.id_turma)} | Aluno: {nomeAluno(insight.id_aluno)}</div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="ia-box">
          <h3>Alertas Inteligentes</h3>
          {carregando ? <p className="texto-ajuda">Carregando alertas...</p> : alertas.length === 0 ? <p className="texto-ajuda">Nenhum alerta registrado.</p> : (
            <div className="ia-lista">
              {alertas.map((alerta) => (
                <article key={alerta.id} className="ia-card ia-card-alerta">
                  <div className="ia-card-topo">
                    <span className={`badge badge-${alerta.status === 'resolvido' ? 'pago' : alerta.status === 'em_andamento' ? 'pendente' : 'vencido'}`}>{alerta.tipo}</span>
                    <select value={alerta.status} onChange={(e) => atualizarStatusAlerta(alerta.id, e.target.value)}>
                      {STATUS_ALERTA.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                    </select>
                  </div>
                  <p>{alerta.mensagem}</p>
                  <div className="ia-meta">Turma: {nomeTurma(alerta.id_turma)} | Aluno: {nomeAluno(alerta.id_aluno)}</div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
